/**
 * settingsMeta.js — todos os parâmetros da tela Configurações
 * CI Sistemas de Informática Ltda. — AcessoiD (Onda 100)
 *
 * Junta a base (`settingsMetaBase.js`, movida por script) com os parâmetros da
 * consolidação por passagem, que até a Onda 99 eram 13 blocos de JSX escritos à
 * mão no meio da página — cada um com seu `<input>`, seu `min`, seu `max`. Aqui
 * eles ganham a mesma forma dos demais, e a página deixa de ter campo "especial".
 *
 * O QUE NÃO MUDOU, e o teste `settingsMeta.test.js` confere contra o inventário da
 * tela antiga: a chave, o tipo, a unidade, os limites, o passo e o valor mostrado
 * quando a chave ainda não existe no banco (`padrao`). Os textos são os mesmos,
 * sem a marcação de negrito.
 */
import { META_BASE } from './settingsMetaBase';

const LIGADO_DESLIGADO = [{ value: '1', label: 'Ligado' }, { value: '0', label: 'Desligado' }];

const META_PASSAGEM = {
  passage_gap_ms: {
    label: 'Fim da passagem',
    description: 'Tempo sem novas leituras da câmera que encerra a passagem e registra a placa consolidada. Define a latência máxima para uma leitura única e fraca.',
    type: 'number', unit: 'ms', min: 1000, max: 10000, step: 500, padrao: '3000',
    hint: 'Recomendado: 3000 ms com captura persistente a 1 frame por segundo.',
  },
  passage_commit_readings: {
    label: 'Leituras para registrar por votação',
    description: 'Com este número de leituras na mesma passagem, a placa é registrada pelo consenso por caractere sem esperar o fim da passagem.',
    type: 'number', unit: 'leituras', min: 2, max: 6, step: 1, padrao: '3',
  },
  passage_strong_min_char: {
    label: 'Confiança mínima por caractere para registro imediato',
    description: 'Uma leitura em que todos os caracteres têm ao menos esta confiança é registrada na primeira leitura, sem latência.',
    type: 'number', unit: '', min: 0.80, max: 0.99, step: 0.01, padrao: '0.95',
  },
  passage_strong_min_width: {
    label: 'Tamanho mínimo da placa para registro imediato',
    description: 'Largura da placa, em pixels, abaixo da qual a leitura não encerra a passagem sozinha, mesmo estando confiante. Ela continua valendo, mas espera os próximos quadros e entra na votação por caractere — o veículo que faz a curva larga aparece primeiro pequeno e de viés, e um quadro depois costuma render a placa bem maior. Se nenhum quadro melhor vier, a leitura registra ao fim da janela; nenhum veículo se perde. Use 0 para desligar o piso.',
    type: 'number', unit: 'px', min: 0, max: 400, step: 5, padrao: '0',
  },
  passage_settle_width: {
    label: 'Tamanho da placa para decidir sem esperar',
    description: 'Largura da placa, em pixels, a partir da qual o sistema registra a leitura imediatamente. Abaixo disso, enquanto a placa continuar crescendo de um quadro para o outro, ele espera — o veículo está se aproximando e o melhor quadro ainda está por vir. É o que evita registrar a placa quando o carro ainda está atravessado no meio da conversão. A espera nunca passa da janela de passagem, e nenhum veículo deixa de ser registrado por causa dela. Use 0 para desligar.',
    type: 'number', unit: 'px', min: 0, max: 600, step: 10, padrao: '0',
  },
  passage_review_max_prop: {
    label: 'Revisão: proporção máxima da placa (largura ÷ altura)',
    description: 'Quando a passagem registra com uma única leitura e a placa está, ao mesmo tempo, mais estreita que esta proporção e mais estreita que a largura abaixo, a leitura vai para a tela Qualidade do OCR marcada como aguardando revisão. Nada muda no registro: o ticket abre do mesmo jeito. É o perfil do veículo que passa colado à câmera e mostra a placa de lado — em 09/09, 7 aberturas em 37 h, 1 delas errada. Use 0 em qualquer um dos dois para desligar.',
    type: 'number', unit: 'proporção', min: 0, max: 3, step: 0.05, padrao: '0',
    hint: 'Recomendado: 0,90 e 130 px. As leituras erradas da câmera 3 têm proporção mediana 0,93; as certas, 1,20.',
  },
  passage_review_max_width: {
    label: 'Revisão: largura máxima da placa',
    description: 'Vale em conjunto com a proporção acima: a leitura só é marcada para revisão quando a placa está abaixo dos dois limites ao mesmo tempo. Use 0 para desligar.',
    type: 'number', unit: 'px', min: 0, max: 600, step: 5, padrao: '0',
  },
  passage_follow_outside_roi: {
    label: 'Acompanhar o veículo fora da zona',
    description: 'Na câmera de entrada o veículo aparece pequeno e de viés, e é justamente quando ele chega perto — a placa grande e de frente — que ele avança para fora da zona desenhada: o melhor quadro era descartado. Com isto ligado, um veículo que já foi admitido dentro da zona continua sendo acompanhado quando atravessa a borda, e o quadro maior entra na decisão. O desenho da zona não muda e continua sendo quem decide quem entra: nenhuma passagem nova nasce de uma detecção de fora, e só entra quadro maior que todos os anteriores — o trânsito ao fundo, sempre pequeno, nunca alcança a passagem.',
    type: 'select', options: LIGADO_DESLIGADO, padrao: '0',
  },
  passage_correct_after_commit: {
    label: 'Corrigir o registro com um quadro melhor',
    description: 'O veículo que começa a entrar de viés é lido com um caractere trocado, o ticket nasce errado, e o quadro alinhado que vem em seguida — quando o carro se enquadra na pista — era jogado fora. Com isto ligado, um quadro que chegue logo depois do registro corrige a placa do ticket quando, ao mesmo tempo, for mais confiante, maior, mais alinhado e diferir da placa registrada só por confusão de caractere (I↔J, O↔D, 0↔O). Troca arbitrária nunca corrige. A correção fica registrada na Qualidade do OCR com autor "passagem", é auditada e pode ser revista pelo operador.',
    type: 'select', options: LIGADO_DESLIGADO, padrao: '0',
  },
  static_plate_seconds: {
    label: 'Placa estática',
    description: 'A mesma placa vista no mesmo lugar por mais que este tempo é considerada um veículo estacionado e deixa de ser registrada até se mover.',
    type: 'number', unit: 'segundos', min: 15, max: 600, step: 1, padrao: '60',
  },
  similarity_window_seconds: {
    label: 'Janela de Similaridade',
    description: 'Período em que uma placa similar é considerada duplicata. Após este tempo, a placa pode ser registrada novamente mesmo que seja parecida com outra anterior.',
    type: 'number', unit: 'segundos', min: 5, max: 300, step: 1, padrao: '30',
    hint: 'Modo legado: a produção usa a consolidação por passagem. Se voltar a este modo, 30 s é o valor de referência; valores muito baixos reduzem a eficácia do filtro.',
  },
  double_read_window_seconds: {
    label: 'Janela de Confirmação',
    description: 'Tempo máximo entre a 1ª e a última leitura para que a placa seja confirmada e registrada. Se o mínimo de leituras não ocorrer dentro deste prazo, a contagem é reiniciada.',
    type: 'number', unit: 'segundos', min: 2, max: 60, step: 1, padrao: '8',
    hint: 'Valor recomendado: 8s. Deve ser maior que o frame_interval configurado.',
  },
  double_read_min_reads: {
    label: 'Mínimo de Leituras para Confirmação',
    description: 'Número mínimo de leituras consecutivas da mesma placa dentro da janela de confirmação para que o acesso seja liberado. Use 2 para confirmação rápida (recomendado com Burst ativo), 3 para alta precisão, ou 4–5 para máxima segurança em condomínios residenciais.',
    type: 'number', unit: 'leituras', min: 2, max: 5, step: 1, padrao: '2',
    hint: 'Com Burst de 3 frames ativo, use 2–3 leituras. A confirmação ocorre em menos de 1 segundo.',
  },
};

/**
 * Onda 102 (fase 3) — o que o servidor LÊ e nenhuma tela mostrava.
 *
 * Dezessete chaves que só mudavam por SQL. Rótulo, unidade, padrão e faixa saíram do
 * código que consome cada uma — `entryFlowWatch.js`, `routes/reconciliation.js`,
 * `manualTicketService.js`, `rtspService.js`, `routes/gate.js`, `routes/app.js` — e o
 * teste `onda102.test.js` confere os limites contra esses consumidores de verdade.
 *
 * POR QUE OS LIMITES IMPORTAM MAIS AQUI: o servidor é tolerante em silêncio. Uma
 * carência de reconciliação de 200 h não dá erro — é IGNORADA, e vale o padrão de 12 h,
 * com a tela mostrando 200. A faixa no campo é o que impede a tela de mostrar um valor
 * que não está valendo.
 *
 * `podeVazio`: chaves que precisam poder voltar a vazio (a rota recusava valor vazio).
 * `vazioVira`: o que gravar quando o campo é esvaziado.
 * `validar(texto)`: devolve a frase da recusa, ou `null`.
 */
export const CHAVES_POR_CAMERA = ['settleWidth', 'strongMinWidth', 'reviewMaxWidth', 'reviewMaxProp', 'reviewSmallWidth', 'reviewSmallProp', 'commitReadings', 'gapMs', 'growthTolerance', 'edgeMarginPx'];

export function validarAjustePorCamera(texto) {
  const t = String(texto || '').trim();
  if (!t || t === '{}') return null;
  let mapa;
  try { mapa = JSON.parse(t); } catch (e) { return 'Não é um JSON válido. Exemplo: {"4":{"settleWidth":120}}'; }
  if (!mapa || typeof mapa !== 'object' || Array.isArray(mapa)) return 'Esperado um objeto: {"id da câmera": {"chave": valor}}.';
  for (const [camera, ajustes] of Object.entries(mapa)) {
    if (!/^\d+$/.test(camera)) return `"${camera}" não é um id de câmera (use o número, como no módulo Câmeras).`;
    if (!ajustes || typeof ajustes !== 'object' || Array.isArray(ajustes)) return `Câmera ${camera}: esperado um objeto {"chave": valor}.`;
    for (const [chave, valor] of Object.entries(ajustes)) {
      if (!CHAVES_POR_CAMERA.includes(chave)) return `Câmera ${camera}: "${chave}" não é um ajuste aceito. Aceitos: ${CHAVES_POR_CAMERA.join(', ')}.`;
      if ((typeof valor !== 'number' && typeof valor !== 'string') || String(valor).trim() === '' || !Number.isFinite(Number(valor)) || Number(valor) < 0) {
        return `Câmera ${camera}, ${chave}: o valor tem de ser um número não negativo.`;
      }
    }
  }
  return null;
}

const VERSAO = /^\d+(\.\d+){0,3}$/;
const validarVersao = (t) => (!String(t || '').trim() || VERSAO.test(String(t).trim()) ? null : 'Use só números e pontos, como 1.2.0.');
const validarUrl = (t) => (!String(t || '').trim() || /^https?:\/\/\S+$/i.test(String(t).trim()) ? null : 'O endereço tem de começar com http:// ou https://.');

const META_FASE3 = {
  // ── Vigia do fluxo de entrada (Onda 95) ──────────────────────────────────
  entry_watch_enabled: {
    label: 'Vigia do fluxo de entrada',
    description: 'Avisa no painel de saúde quando a entrada para de abrir tickets com as câmeras e o motor funcionando — foi o que aconteceu em 19/09/2026, quando a câmera de entrada foi inclinada e o sábado registrou 17 entradas contra 232 do anterior, sem nenhum alarme. A testemunha é a câmera de saída: todo carro que entrou sem ticket sai como "saída sem ticket". Desligado, o serviço aparece como "desligado" e não conta para a saúde geral.',
    type: 'toggle', padrao: 'true', recommended: true,
  },
  entry_watch_window_min: {
    label: 'Janela observada',
    description: 'Período, contado para trás a partir de agora, em que o vigia conta entradas e saídas. Janelas menores reagem mais cedo e alarmam mais à toa; o servidor não aceita menos de 15 minutos. A comparação com o "normal" usa a mesma janela, no mesmo dia da semana, nas quatro semanas anteriores.',
    type: 'number', unit: 'min', min: 15, max: 1440, step: 5, padrao: '60', recommended: 60,
  },
  entry_watch_min_exits: {
    label: 'Saídas mínimas para a saída testemunhar',
    description: 'Com menos saídas que isto na janela, a câmera de saída não prova nada (madrugada, domingo cedo) e o alarme de entrada muda não dispara.',
    type: 'number', unit: 'saídas', min: 0, max: 200, step: 1, padrao: '6', recommended: 6,
  },
  entry_watch_orphan_ratio: {
    label: 'Fração de saídas sem ticket que caracteriza a pane',
    description: 'Medido no G2: num dia normal de 0% a 20% das saídas não têm ticket; no dia da pane, de 75% a 100% desde a primeira hora. Com 0,60 o alarme exige que 60% das saídas da janela estejam sem ticket. É uma fração: 0,60 = 60%.',
    type: 'number', unit: 'fração', min: 0, max: 1, step: 0.05, padrao: '0.6', recommended: 0.6,
  },
  entry_watch_confirmed_ratio: {
    label: 'Entrada "sem abrir": fração do normal, com a saída acusando',
    description: 'O alarme vermelho (entrada muda) exige os dois lados: saídas sem ticket em alta E entradas abaixo desta fração do normal. É mais larga que a régua abaixo de propósito: às 10 h do dia da pane entraram 5 de um normal de 16 (31%) — acima de 30%, e era pane. Sem histórico para o horário, a comparação é com o número de saídas.',
    type: 'number', unit: 'fração', min: 0, max: 1, step: 0.05, padrao: '0.5', recommended: 0.5,
  },
  entry_watch_ratio: {
    label: 'Entrada "fraca": fração do normal, sem a saída acusar',
    description: 'Alarme amarelo: entradas abaixo desta fração do normal do horário, mesmo sem saídas sem ticket. Cobre a manhã, quando ainda ninguém saiu para testemunhar.',
    type: 'number', unit: 'fração', min: 0, max: 1, step: 0.05, padrao: '0.3', recommended: 0.3,
  },
  entry_watch_min_reference: {
    label: 'Movimento mínimo para haver "normal"',
    description: 'Se a mediana das quatro semanas anteriores, no mesmo horário, for menor que isto, não há "normal" com que comparar e o alarme amarelo não dispara. Evita alarme em horário de pouco movimento, em que 1 entrada em vez de 3 não significa nada.',
    type: 'number', unit: 'entradas', min: 0, max: 500, step: 1, padrao: '8', recommended: 8,
  },

  // ── Regras de operação ───────────────────────────────────────────────────
  reconciliation_grace_hours: {
    label: 'Carência da Reconciliação',
    description: 'Tempo que um ticket aberto tem antes de a falta de saída aparecer na tela Reconciliação como "saída nunca vista". Sem carência, o carro que entrou há cinco minutos e está na vaga aparecia como anomalia — em 10/09/2026 a tela mostrava 944 casos, 32 deles veículos que tinham acabado de chegar. Medido no G2: 90% dos veículos saem em até 12 h. Errar para o lado longo é mais barato: só atrasa a descoberta de um caso sem urgência. O servidor só aceita de 0 a 168 h; fora disso ignora o valor e usa 12 h.',
    type: 'number', unit: 'h', min: 0, max: 168, step: 1, padrao: '12', recommended: 12,
  },
  manual_ticket_dedupe_minutes: {
    label: 'Ticket manual: janela de "recente demais"',
    description: 'Ao abrir um ticket à mão (app da guarita ou tela Tickets), o sistema recusa se já houve ticket da mesma placa nos últimos minutos, mesmo fechado: é o toque duplo do operador, ou o colega que já criou. A janela é curta porque sair e voltar em vinte minutos é caso real. Ticket ABERTO da mesma placa é sempre recusado, independentemente deste valor. Use 0 para desligar a janela.',
    type: 'number', unit: 'min', min: 0, max: 1440, step: 1, padrao: '15', recommended: 15,
  },

  // ── Consolidação por passagem: avançado ──────────────────────────────────
  passage_growth_tolerance: {
    label: 'Crescimento mínimo para "ainda se aproximando"',
    description: 'Enquanto a placa cresce mais que esta fração de um quadro para o outro, a passagem entende que o veículo ainda está chegando e espera o próximo quadro (ver "Tamanho da placa para decidir sem esperar"). Com 0,08, a placa precisa crescer mais de 8% para contar como aproximação. O servidor limita a faixa a 0,01–0,5.',
    type: 'number', unit: 'fração', min: 0.01, max: 0.5, step: 0.01, padrao: '0.08', recommended: 0.08,
  },
  passage_per_camera: {
    label: 'Ajustes da passagem por câmera (JSON)',
    description: `Os limites da consolidação foram calibrados na câmera de ENTRADA. A de saída aponta para baixo e vê a placa menor (156 px em média contra 223), e as duas têm resoluções diferentes (1920 e 1280 px): o mesmo número de pixels significa tamanhos reais diferentes em cada uma. Aqui uma câmera ganha limites próprios: {"4":{"settleWidth":120,"commitReadings":4}}. Chaves aceitas: ${CHAVES_POR_CAMERA.join(', ')}. Atenção ao sinal de settleWidth: é um piso de ESPERA — quanto maior, mais a passagem aguarda antes de decidir; baixá-lo faz decidir mais cedo, com menos quadros. Vazio, todas as câmeras usam os valores gerais acima.`,
    type: 'text', padrao: '', podeVazio: true, vazioVira: '{}', validar: validarAjustePorCamera,
    placeholder: '{"4":{"settleWidth":120}}',
  },

  // ── Câmeras ──────────────────────────────────────────────────────────────
  camera_heartbeat_timeout_seconds: {
    label: 'Câmera sem sinal',
    description: 'Tempo sem captura a partir do qual uma câmera é dada como muda. Acende a faixa vermelha "Câmera sem imagem" no topo de todas as telas da plataforma — com a causa provável, testada pelo servidor — e a faixa de contingência no app da guarita, avisando que as entradas precisam ser abertas à mão. O sinal é a última captura, não o cadastro: câmera marcada como ativa, ou em erro, e parada há mais que isto é câmera muda; só a desativada fica de fora. Valor baixo demais alarma a cada reconexão da câmera, que pode levar até alguns minutos.',
    type: 'number', unit: 's', min: 30, max: 3600, step: 10, padrao: '120', recommended: 120,
  },
  observation_window_seconds: {
    label: 'Janela para juntar as leituras da observação',
    description: 'Tempo em que as leituras da mesma placa na zona de observação contam como o mesmo veículo passando. Trabalha junto com "Leituras para Confirmar a Saída": com 2 leituras e 20 s, o veículo precisa ser lido duas vezes em até 20 segundos. O servidor não aceita menos de 5 s.',
    type: 'number', unit: 's', min: 5, max: 300, step: 1, padrao: '20', recommended: 20,
  },

  // ── App da guarita ───────────────────────────────────────────────────────
  app_version: {
    label: 'Versão publicada',
    description: 'A versão do app que está disponível para instalar. O app compara com a dele e, se esta for maior, avisa o operador — ele não baixa nem instala sozinho. Vazio significa "não há distribuição configurada": nenhum aparelho avisa nada. Só informe um número MAIOR que o do instalador anterior depois de publicar o APK novo no servidor: o aviso aparece em todos os aparelhos na hora.',
    type: 'text', padrao: '', podeVazio: true, validar: validarVersao, placeholder: '1.2.0',
  },
  app_min_version: {
    label: 'Versão mínima (atualização obrigatória)',
    description: 'Abaixo desta versão o app insiste no aviso e não deixa dispensar. É para quando a atualização corrige algo que afeta o dinheiro ou a cancela, e conviver com a versão antiga não é opção. Vazio: nenhuma versão é obrigatória.',
    type: 'text', padrao: '', podeVazio: true, validar: validarVersao, placeholder: '1.0.0',
  },
  app_update_notes: {
    label: 'O que mudou (aparece no aviso)',
    description: 'Uma frase curta, para o operador: o que ele ganha ao atualizar. Aparece junto do aviso de versão nova.',
    type: 'text', padrao: '', podeVazio: true, placeholder: 'Leitura do QR do ticket no caixa',
  },
  app_apk_url: {
    label: 'Onde o operador baixa o instalador',
    description: 'O endereço que o aviso abre no navegador do aparelho. O instalador é servido pela própria plataforma e exige login: o endereço da Central de Ajuda deste servidor é o que funciona (o operador entra com o usuário dele e toca em "Baixar"). Sem endereço, o app não avisa — seria oferecer o que não se pode baixar.',
    type: 'text', padrao: '', podeVazio: true, validar: validarUrl, placeholder: 'https://g2.acessoid.com.br/ajuda',
  },
};

export const CHAVES_DA_FASE_3 = Object.keys(META_FASE3);

/**
 * Onda 103 — a segunda regra do selo "aguardando revisão": placa pequena E de viés, na
 * câmera de entrada. Os números das descrições são a medição de 20/09/2026 no G2 (2.366
 * aberturas revisadas). O worker limita a largura a 0–600 e a proporção a 0–3.
 */
const META_ONDA103 = {
  passage_review_small_width: {
    label: 'Revisão na entrada: largura da placa pequena',
    description: 'Na câmera de ENTRADA, a abertura em que a placa está, ao mesmo tempo, mais estreita que esta largura e com proporção abaixo da de baixo vai para a tela Qualidade do OCR marcada como aguardando revisão — com qualquer número de leituras na passagem. Nada muda no registro: o ticket abre do mesmo jeito. Medido no G2 em 30 dias: abaixo de 110 px, as placas DE FRENTE erraram 1 em 74 (1,4%) e as DE VIÉS erraram 15 em 31 (48%); acima de 110 px, 0,6%. É o carro lido ainda atravessado no começo da conversão. Marcar só pela largura mandaria 105 leituras para revisão para achar 16 erros; com as duas condições são 31 para achar 15 — cerca de uma por dia. A câmera de saída fica de fora: ali, placa pequena lê certo (2 erros em 122). Use 0 em qualquer um dos dois para desligar.',
    type: 'number', unit: 'px', min: 0, max: 600, step: 5, padrao: '0', recommended: 110,
  },
  passage_review_small_prop: {
    label: 'Revisão na entrada: proporção que conta como "de viés"',
    description: 'Largura dividida pela altura da placa na imagem. Uma placa vista de frente tem proporção de 1,20 para cima; abaixo disso ela está de viés, e é aí que o OCR troca caractere com confiança alta. Vale em conjunto com a largura de cima: a leitura só é marcada quando está abaixo dos dois limites. Use 0 para desligar.',
    type: 'number', unit: 'proporção', min: 0, max: 3, step: 0.05, padrao: '0', recommended: 1.2,
  },
};

export const CHAVES_DA_ONDA_103 = Object.keys(META_ONDA103);

/**
 * Onda 117 — avisos pelo Telegram. De 25 a 27/09/2026 o Ipê passou três períodos sem
 * registrar entradas (câmera na sexta e no sábado, GPU no domingo) e nas três quem
 * percebeu foi o Iatagã, olhando a tela. O token do bot NÃO é campo desta tela: fica
 * no .env do servidor. Os padrões são os de `services/alertWatch.js` (`opcoesDe`).
 */
const DESTINO = /^-?\d{1,20}$/;
const validarDestinos = (t) => {
  const itens = String(t || '').split(/[\s,;]+/).filter(Boolean);
  const ruim = itens.find(x => !DESTINO.test(x));
  return ruim ? `"${ruim}" não é um id do Telegram. Use "Procurar inscritos" acima para escolher as pessoas.` : null;
};
const JANELA = /^([01]?\d|2[0-3]):[0-5]\d\s*-\s*([01]?\d|2[0-3]):[0-5]\d$/;
const validarJanela = (t) => (!String(t || '').trim() || JANELA.test(String(t).trim()) ? null : 'Use o formato 06:00-22:00 (pode virar a meia-noite, como 22:00-06:00). Vazio = 24 horas.');

const META_ONDA117 = {
  alert_telegram_enabled: {
    label: 'Avisos pelo Telegram',
    description: 'Manda uma mensagem pelo Telegram, a cada pessoa escolhida, quando uma câmera fica sem imagem, quando o reconhecimento de placas para (GPU fora, motor travado) e, no estacionamento, quando a entrada para de abrir tickets — e outra quando volta. Desligado, nada é enviado. Antes de ligar, escolha quem recebe e mande a mensagem de teste.',
    type: 'toggle', padrao: 'false', recommended: true,
  },
  alert_telegram_chat_id: {
    label: 'Quem recebe os avisos',
    description: 'Os ids do Telegram de quem recebe, separados por vírgula. Cada pessoa abre o bot no Telegram e toca em "Iniciar"; depois ela aparece em "Procurar inscritos" acima para ser adicionada com um clique. Quem sair da equipe é removido ali mesmo. Também aceita o id de um grupo (negativo), se o bot estiver nele.',
    type: 'text', padrao: '', podeVazio: true, validar: validarDestinos, placeholder: '1000000001, 1234567890',
  },
  alert_site_name: {
    label: 'Nome do site nos avisos',
    description: 'Aparece no começo de cada mensagem, para quem recebe avisos de mais de um lugar saber de onde é. Vazio: o nome do cliente (Operação) e, sem ele, o nome da vertical.',
    // Onda 134c: o exemplo era "Ipê Clube" fixo, e no G2 parecia que o nome errado estava em uso.
    // O exemplo passa a ser o que de fato vale com o campo vazio: o nome do cliente desta instalação.
    type: 'text', padrao: '', podeVazio: true, placeholder: (s) => String((s && s.client_name) || '').trim() || 'nome do cliente (Operação)',
  },
  alert_wait_minutes: {
    label: 'Espera antes do primeiro aviso',
    description: 'Minutos em falha antes de mandar a mensagem. Uma reconexão da câmera leva de segundos a poucos minutos e se resolve sozinha: em 25/09 houve quedas de 3 e 7 minutos que não pediam ninguém. Menos que isto avisa à toa; muito mais atrasa o aviso da falha que importa.',
    type: 'number', unit: 'min', min: 1, max: 120, step: 1, padrao: '5', recommended: 5,
  },
  alert_reminder_hours: {
    label: 'Lembrete enquanto a falha continuar',
    description: 'A cada quantas horas repetir o aviso de uma falha que não se resolveu. 0 = só o primeiro aviso e o de volta.',
    type: 'number', unit: 'h', min: 0, max: 72, step: 1, padrao: '4', recommended: 4,
  },
  alert_hours: {
    label: 'Horário dos avisos',
    description: 'Fora deste horário nada é enviado: o aviso espera o horário começar e, se a falha passou antes disso sem ter sido avisada, não gera mensagem. Em 26 e 27/09 a câmera do Ipê voltou sozinha às 05:08 e às 02:15 — sem horário definido, essas voltas chegariam de madrugada. Formato 06:00-22:00; vazio = 24 horas.',
    type: 'text', padrao: '', podeVazio: true, validar: validarJanela, placeholder: '06:00-22:00',
  },
};

export const CHAVES_DA_ONDA_117 = Object.keys(META_ONDA117);

/**
 * Onda 119 — a integração não perde os eventos de um fim de semana. Em 26 e 27/09/2026
 * o receptor do Ipê ficou desligado; com 10 tentativas (~21 h) a fila desistiu de 406
 * eventos horas antes de o receptor voltar. Padrões de `clubSocialService.politicaDeEntrega`
 * e de `alertWatch.opcoesDe`.
 */
const META_ONDA119 = {
  integration_retry_window_hours: {
    label: 'Continuar tentando por',
    description: 'Horas em que um evento continua sendo tentado (a cada 4 h) mesmo depois de esgotadas as tentativas. Com 72 h, um receptor desligado de sexta à noite até segunda de manhã ainda recebe tudo. Antes desta regra a fila desistia em cerca de 21 h — foi o que perdeu os 406 eventos do fim de semana de 26 e 27/09 no Ipê. 0 = só as tentativas.',
    type: 'number', unit: 'h', min: 0, max: 720, step: 1, padrao: '72', recommended: 72,
  },
  integration_rescue_days: {
    label: 'Recuperar eventos não entregues dos últimos',
    description: 'Quando uma entrega volta a dar certo — o receptor voltou —, os eventos em que a plataforma tinha desistido por falha de conexão (máquina desligada, porta fechada, sem resposta) nestes dias voltam à fila sozinhos e são enviados em ordem, do mais antigo para o mais novo. O que o receptor recusou com erro não volta: ali ele respondeu, e reenviar não mudaria a resposta. 0 = desligado.',
    type: 'number', unit: 'dias', min: 0, max: 30, step: 1, padrao: '7', recommended: 7,
  },
  alert_integration_minutes: {
    label: 'Aviso de integração parada',
    description: 'Clube e condomínio: quando o evento mais antigo da fila passa deste tempo sem ser entregue, os avisos pelo Telegram dizem que a integração parou, quantos eventos esperam e o motivo (por exemplo, "a máquina do receptor não está na rede"). Nenhum evento se perde enquanto isso: eles saem quando o receptor voltar.',
    type: 'number', unit: 'min', min: 10, max: 1440, step: 5, padrao: '60', recommended: 60,
  },
};

export const CHAVES_DA_ONDA_119 = Object.keys(META_ONDA119);

/**
 * Onda 181 (19/10/2026) — a validade do evento. O receptor do Ipê devolveu HTTP 500 a 12 eventos de
 * entrada e a janela de 72 h da Onda 119 (feita para o receptor desligado) reentregou às 15:33 uma
 * entrada das 10:11: o sistema do clube, que imprime o ticket ao receber, imprimiu de novo.
 */
const META_ONDA181 = {
  integration_entry_event_ttl_min: {
    label: 'Validade do evento de entrada',
    description: 'Por quantos minutos a plataforma ainda tenta entregar um evento de ENTRADA ao sistema de gestão. Passado o prazo sem entrega (receptor fora do ar ou respondendo erro), o evento expira e não é mais enviado: a entrada continua registrada na plataforma e nos relatórios, e o evento aparece na Supervisão com o motivo "fora da validade". Evita que um ticket seja impresso horas depois, ou duas vezes. 0 = sem validade (tenta por até 72 horas, como antes).',
    type: 'number', unit: 'min', min: 0, max: 1440, step: 1, padrao: '2', recommended: 2,
  },
  integration_exit_event_ttl_min: {
    label: 'Validade do evento de saída',
    description: 'O mesmo prazo para o evento de SAÍDA. Passado o prazo sem entrega, expira e o sistema de gestão não recebe a saída (o ticket dele fica aberto até a guarita resolver). 0 = sem validade.',
    type: 'number', unit: 'min', min: 0, max: 1440, step: 1, padrao: '2', recommended: 2,
  },
};

export const CHAVES_DA_ONDA_181 = Object.keys(META_ONDA181);

/**
 * Onda 135 — a saída "na tolerância" nos relatórios de acesso. Regra do cliente (o
 * estacionamento do clube não cobra quem só passa), por isso parâmetro. Não é a tolerância do
 * estacionamento (`exit_tolerance_min`, tempo para sair depois de pagar).
 */
const META_ONDA135 = {
  access_tolerance_min: {
    // Onda 178: o nome diz o que a régua decide — ela já valia para o evento de saída, a liberação e o âmbar.
    label: 'Tolerância de saída sem pagamento',
    description: 'Minutos entre a entrada e a saída lidas da mesma placa em que a saída é livre, sem pagamento. Dentro deste tempo, com a "Saída livre na tolerância" ligada: o evento de saída enviado ao sistema de gestão vai com within_tolerance = true, a liberação da saída abre por tolerância, o semáforo da guarita acende o âmbar e os Relatórios de Acesso mostram o selo "na tolerância". Só a saída lida pela câmera conta; sem entrada lida nas 24 h anteriores, não há permanência.',
    type: 'number', unit: 'min', min: 1, max: 1440, step: 1, padrao: '15', recommended: 15,
  },
};

export const CHAVES_DA_ONDA_135 = Object.keys(META_ONDA135);

/**
 * Onda 178 (18/10/2026) — o liga/desliga da saída livre na tolerância, ao lado dos minutos. Pedido do
 * Iatagã: poder fazer toda saída depender de liberação (pagamento ou autorização de saída sem
 * pagamento; o visitante autorizado continua liberando), sem perder o valor dos minutos.
 */
const META_ONDA178 = {
  access_tolerance_enabled: {
    label: 'Saída livre na tolerância',
    description: 'Ligado (padrão): a saída lida até a tolerância abaixo depois da entrada da mesma placa é livre, sem pagamento. Desligado: nenhuma saída é "na tolerância" — o evento de saída vai ao sistema de gestão com within_tolerance = false (tolerance_minutes = 0), a liberação da saída só abre com pagamento registrado, autorização de saída sem pagamento ou visitante com autorização de visita válida, o semáforo não acende o âmbar e os Relatórios de Acesso deixam de mostrar o selo. Vale em até 30 s, sem reiniciar.',
    type: 'select', options: LIGADO_DESLIGADO, padrao: '1', recommended: '1',
  },
};

export const CHAVES_DA_ONDA_178 = Object.keys(META_ONDA178);

/**
 * Onda 137 (09/10/2026) — o carro parado no mesmo lugar não registra de novo. A regra da placa
 * estática já existia (`static_plate_seconds`), mas esquecia a placa 30 s depois de deixar de
 * vê-la, e um carro passando na frente do estacionado zerava a memória: uma placa do pátio ao
 * fundo da entrada do Ipê registrou 47 vezes em 08/10. As duas chaves moram ao lado dela.
 */
const META_ONDA137 = {
  static_plate_forget_seconds: {
    label: 'Placa parada: esquecer depois de',
    // Onda 176 (17/10/2026): esta chave também é a janela em que o cadastrado que entrou e não saiu é reconhecido
    // parado adiante (Onda 171, R3/R4). Com 1800 s o FOF0512, relido 89 min depois como FQF0512, virou visitante.
    description: 'Por quanto tempo, sem sair por uma câmera de saída, um veículo cadastrado que entrou é reconhecido quando aparece parado ou estacionado à vista da câmera de entrada — inclusive lido com um ou dois caracteres trocados (o FOF0512 relido como FQF0512). Nesse tempo ele não vira uma entrada nova nem um visitante; só vale para a placa pequena (carro parado adiante), nunca para quem passa perto da câmera entrando. O mesmo tempo vale para esquecer a placa parada no mesmo lugar. A saída do veículo apaga a memória. Recomendado: 43200 s (12 h). Com 1800 s (30 min), o associado estacionado à vista volta a ser registrado depois de meia hora.',
    type: 'number', unit: 'segundos', min: 15, max: 86400, step: 15, padrao: '30', recommended: 43200,
  },
  static_plate_after_commit: {
    label: 'Placa registrada fica lembrada no lugar',
    description: 'Ligado: a placa que acabou de ser registrada fica lembrada no lugar em que foi lida; vista de novo ali é o mesmo carro parado e não registra até se mover ou sumir pelo tempo acima. A saída da placa pela outra câmera apaga a memória. Desligado (padrão): comportamento de antes — o carro parado no portão ou no pátio pode registrar de novo quando a passagem termina.',
    type: 'select', options: LIGADO_DESLIGADO, padrao: '0', recommended: '1',
  },
};

export const CHAVES_DA_ONDA_137 = Object.keys(META_ONDA137);

/**
 * Onda 139 (10/10/2026) — a leitura confere com quem a plataforma conhece. Saída do Ipê, 17:26:
 * GID0259, GIO0259 e GIQ0259 em 4 s para o mesmo carro; só GIO0259 existe (cadastro e entrada às
 * 14:18). A conferência usa a confiança de cada caractere: a candidata tem de bater nas posições
 * seguras e pode diferir só nas inseguras.
 */
const META_ONDA139 = {
  plate_match_known: {
    label: 'Conferir a leitura com o cadastro',
    description: 'Ligado: antes de registrar, a placa lida é confrontada com o cadastro (associados no clube, moradores no condomínio) e, na câmera de saída, com quem entrou nas últimas 24 h. Se a placa lida não existe e UMA placa conhecida bate em todas as posições em que o OCR está seguro e difere só nas inseguras (por confusão de caractere, ou por qualquer coisa quando a confiança é muito baixa), o registro sai com a placa conhecida; a leitura bruta fica guardada e a correção aparece na Qualidade do OCR como "conferencia". Mais de uma candidata: fica como leu, marcada para revisão. Desde 19/10/2026, com a chave ligada: a placa que NÃO é conhecida não registra no primeiro quadro, espera a passagem fechar (até 3 s) para só a placa confirmada ir ao sistema de gestão; se a passagem leu também uma placa conhecida parecida, a conhecida vence; lida uma vez só e a 1 caractere de uma placa conhecida, registra como leu, marcada para revisão ("vizinho_do_cadastro"). Desligado (padrão): nada muda.',
    type: 'select', options: LIGADO_DESLIGADO, padrao: '0', recommended: '1',
  },
  plate_match_min_char: {
    label: 'Conferência: confiança que torna a posição segura',
    description: 'Confiança por caractere a partir da qual a posição é "segura" e precisa bater na candidata. Abaixo disso a posição é insegura e pode ser corrigida. 0,95 é o mesmo piso da leitura forte.',
    type: 'number', min: 0.5, max: 1, step: 0.01, padrao: '0.95', recommended: 0.95,
  },
};

export const CHAVES_DA_ONDA_139 = Object.keys(META_ONDA139);

/**
 * Onda 140 (10/10/2026) — o sentido da saída. Um associado estacionando de ré atravessou o quadro
 * da câmera de saída do Ipê em 2 s e foi registrado como saída. O portão é só de saída: quem sai
 * para no portão e depois se afasta; quem corre de lado está manobrando no pátio.
 */
const META_ONDA140 = {
  exit_direction_mode: {
    label: 'Sentido da saída',
    description: 'Na câmera de saída, a trajetória da placa (os quadros da passagem mais as detecções dos segundos anteriores) diz se o carro parou no portão ou se afastou — saída — ou se correu de lado — manobra no pátio. Desligado: nada muda. Sombra: a manobra é só marcada (selo "manobra (sombra)" na Qualidade do OCR e linha no log), para calibrar contra as saídas reais antes de ligar. Ligado: a manobra não registra saída nem avisa o sistema do clube. Passagem sem trajetória (um quadro só e nada antes) registra como hoje.',
    type: 'select', options: [{ value: 'desligado', label: 'Desligado' }, { value: 'sombra', label: 'Sombra (só marca)' }, { value: 'ligado', label: 'Ligado' }], padrao: 'desligado', recommended: 'sombra',
  },
  exit_direction_lateral_max: {
    label: 'Sentido da saída: deslocamento lateral máximo',
    description: 'Em % da largura do quadro por segundo. Acima disso, sem a placa encolher ou subir, o carro está cruzando o quadro de lado: manobra. O caso de 10/10 no Ipê cruzou a 22% por segundo; quem para no portão fica abaixo de 3%.',
    type: 'number', unit: '% / s', min: 1, max: 50, step: 1, padrao: '8', recommended: 8,
  },
  exit_direction_still_max: {
    label: 'Sentido da saída: parada',
    description: 'Deslocamento do centro da placa, em % do quadro, até o qual ela conta como parada no portão.',
    type: 'number', unit: '%', min: 1, max: 20, step: 1, padrao: '3', recommended: 3,
  },
};

export const CHAVES_DA_ONDA_140 = Object.keys(META_ONDA140);

const SENTIDOS_DA_SAIDA = ['direita', 'baixo_direita', 'baixo', 'baixo_esquerda', 'esquerda', 'cima_esquerda', 'cima', 'cima_direita'];

/** Onda 163: o JSON do sentido esperado por câmera. Devolve a mensagem do erro, ou null. */
export function validarSentidoPorCamera(texto) {
  const t = String(texto || '').trim();
  if (!t || t === '{}') return null;
  let mapa;
  try { mapa = JSON.parse(t); } catch (e) { return 'Não é um JSON válido. Exemplo: {"5":{"modo":"sombra","sentido":"baixo_esquerda"}}'; }
  if (!mapa || typeof mapa !== 'object' || Array.isArray(mapa)) return 'Esperado um objeto: {"id da câmera": {"sentido": ...}}.';
  for (const [camera, regra] of Object.entries(mapa)) {
    if (!/^\d+$/.test(camera)) return `"${camera}" não é um id de câmera (use o número, como no módulo Câmeras).`;
    if (!regra || typeof regra !== 'object' || Array.isArray(regra)) return `Câmera ${camera}: esperado um objeto {"modo": ..., "sentido": ...}.`;
    for (const chave of Object.keys(regra)) {
      if (!['modo', 'sentido', 'tolerancia'].includes(chave)) return `Câmera ${camera}: "${chave}" não é aceito. Aceitos: modo, sentido, tolerancia.`;
    }
    if (!SENTIDOS_DA_SAIDA.includes(String(regra.sentido || ''))) return `Câmera ${camera}: o sentido tem de ser um destes: ${SENTIDOS_DA_SAIDA.join(', ')}.`;
    if (regra.modo !== undefined && !['desligado', 'sombra', 'ligado'].includes(String(regra.modo))) return `Câmera ${camera}: o modo tem de ser desligado, sombra ou ligado.`;
    if (regra.tolerancia !== undefined && !(Number(regra.tolerancia) > 0 && Number(regra.tolerancia) <= 180)) return `Câmera ${camera}: a tolerância vai de 1 a 180 graus.`;
  }
  return null;
}

/**
 * Onda 163 (15/10/2026) — o sentido esperado da saída, por câmera. Na CAM03-Saída do Ipê quem sai
 * VEM em direção à câmera, rumo à cancela; quem manobra para as duas vagas da direita anda para a
 * direita. A régua da 140 (quem sai se afasta) não serve a essa câmera.
 */
const META_ONDA163 = {
  exit_direction_per_camera: {
    label: 'Sentido esperado por câmera (JSON)',
    description: `Para a câmera de saída em que quem sai anda sempre no mesmo rumo pelo quadro. A plataforma compara o deslocamento da placa, do primeiro ao último quadro visto, com esse rumo: dentro da tolerância (67,5° para cada lado, se não informada) é saída; fora, seria manobra no pátio. Exemplo: {"5":{"modo":"sombra","sentido":"baixo_esquerda"}}. Sentidos: ${SENTIDOS_DA_SAIDA.join(', ')} (como se vê na imagem da câmera). Modo sombra: registra tudo como hoje e só escreve no log o que faria — para medir antes de ligar. Ligado: a manobra não registra saída, não acende o semáforo e não avisa o sistema de gestão. A câmera listada aqui deixa de usar o "Sentido da saída" geral acima; as demais seguem com ele. Vazio, nada muda.`,
    type: 'text', padrao: '', podeVazio: true, vazioVira: '{}', validar: validarSentidoPorCamera,
    placeholder: '{"5":{"modo":"sombra","sentido":"baixo_esquerda"}}',
  },
};

export const CHAVES_DA_ONDA_163 = Object.keys(META_ONDA163);

/**
 * Onda 166 (15/10/2026) — a saída liberada pelo sistema de pagamento do cliente (clube ou condomínio
 * com cobrança própria do estacionamento, integrada por webhook: o Ipê Clube). Dois interruptores:
 * a liberação pela integração (a API recebe os pagamentos e a plataforma decide a cada saída) e a
 * abertura automática (aciona os relés da controladora de saída).
 */
const META_ONDA166 = {
  exit_payment_release_enabled: {
    label: 'Liberação da saída pelo sistema de pagamento',
    description: 'Para a instalação cujo sistema de gestão cobra o estacionamento. Ligado: o sistema de gestão registra cada pagamento pela API (escopo "Liberação da saída — Registro do pagamento") e, a cada placa lida na câmera de saída, a plataforma decide se a saída é liberada — dentro da tolerância depois da entrada, visitante com autorização válida, ou pagamento registrado dentro do prazo. A decisão vai no evento de saída enviado ao sistema de gestão (bloco gate). Sem a abertura automática abaixo, nada é acionado: é a observação, para conferir as decisões com as saídas reais. Desligado (padrão): a API recusa os pagamentos e nada muda.',
    type: 'select', options: LIGADO_DESLIGADO, padrao: '0',
  },
  exit_auto_open_enabled: {
    label: 'Abrir cancela e portão automaticamente',
    description: 'Só vale com a liberação acima ligada. Ligado: quando a saída é liberada, a plataforma manda a controladora de saída pulsar o relé da câmera que leu a placa (abre a cancela ou o portão, com um bipe longo) e, quando a saída é recusada, manda o bloqueio (dois bipes curtos, nenhum relé). O vínculo de cada câmera de saída com a controladora e o relé é a tela Câmeras ("Liberação automática de saída"); câmera sem controladora nunca abre. O evento de saída leva published=true só quando a controladora confirma o pulso. Desligado (padrão): só observação. A abertura manual continua pela botoeira da guarita.',
    type: 'select', options: LIGADO_DESLIGADO, padrao: '0',
  },
  exit_authorization_window_min: {
    label: 'Prazo para sair depois do pagamento',
    description: 'Quando o sistema de gestão não informa até quando a saída vale, ela vale por este tempo depois do pagamento. Passado o prazo, a saída não é liberada e a guarita resolve.',
    type: 'number', unit: 'min', min: 1, max: 1440, step: 1, padrao: '30', recommended: 30,
  },
};

export const CHAVES_DA_ONDA_166 = Object.keys(META_ONDA166);

/**
 * Onda 141 (12/10/2026) — o Semáforo da Guarita. Na bancada da guarita, uma controladora com duas
 * lâmpadas: verde para associado ativo ou morador, vermelho (com bipe) para todo o resto. O
 * liga/desliga é por câmera (tela Câmeras); aqui fica o broker e o sinal. A senha do broker é
 * gravada pelo bloco próprio (nunca aparece).
 */
const validarMqtt = (t) => (!String(t || '').trim() || /^mqtts?:\/\/\S+$/i.test(String(t).trim()) ? null : 'O endereço tem de começar com mqtt:// (ou mqtts://), ex. mqtt://192.168.2.122:1884.');
const validarPrefixo = (t) => {
  const v = String(t || '').trim();
  if (!v) return 'O prefixo não pode ficar vazio.';
  if (/[\s+#]/.test(v) || v.startsWith('/') || v.endsWith('/')) return 'Só níveis separados por "/", sem espaço, "+" ou "#", e sem barra no início ou no fim.';
  return null;
};
const META_ONDA141 = {
  semaforo_mqtt_url: {
    label: 'Broker MQTT do semáforo',
    description: 'Endereço do broker Mosquitto que roda no servidor desta instalação, na rede local da guarita. Vazio: o semáforo não envia nada, mesmo ligado numa câmera.',
    type: 'text', padrao: '', podeVazio: true, validar: validarMqtt, placeholder: 'mqtt://192.168.2.122:1884',
  },
  semaforo_mqtt_username: {
    label: 'Usuário da plataforma no broker',
    description: 'O usuário com que a plataforma publica os sinais. A senha é gravada no bloco acima e nunca é exibida.',
    type: 'text', padrao: '', podeVazio: true, placeholder: 'plataforma',
  },
  semaforo_topic_prefix: {
    label: 'Prefixo dos tópicos',
    description: 'A controladora de cada câmera assina <prefixo>/<código da controladora>/sinal. O mesmo prefixo vai no config.h da controladora (SITE).',
    type: 'text', padrao: 'acessoid/site/semaforo', validar: validarPrefixo, placeholder: 'acessoid/ipe/semaforo',
  },
  semaforo_pulse_ms: {
    label: 'Tempo que a cor fica acesa',
    description: 'A cada leitura, a lâmpada acende por este tempo e apaga sozinha; uma leitura nova sobrepõe a anterior. Chega à controladora pela configuração retida.',
    type: 'number', unit: 'ms', min: 500, max: 30000, step: 100, padrao: '3000', recommended: 3000,
  },
  semaforo_buzzer: {
    label: 'Bipe no vermelho',
    description: 'Ligado: dois bipes curtos a cada vermelho (não associado, não morador). O verde é sempre silencioso. Desligado: só a lâmpada.',
    type: 'select', options: LIGADO_DESLIGADO, padrao: '1', recommended: '1',
  },
};

export const CHAVES_DA_ONDA_141 = Object.keys(META_ONDA141);

// Onda 123: o nome do cliente. A descrição mora em META_BASE, ao lado do Tipo de Operação,
// porque é lá que ele aparece na tela; a lista existe para o inventário saber de onde veio.
export const CHAVES_DA_ONDA_123 = ['client_name'];

// Onda 154: a cobrança habilitada (clube e condomínio que cobram ticket). Mesma regra do nome do
// cliente: a descrição mora em META_BASE, na seção Operação; a lista é para o inventário.
export const CHAVES_DA_ONDA_154 = ['cobranca_habilitada'];

/**
 * Onda 101 — textos que ficaram para trás da produção. A base (`settingsMetaBase.js`)
 * continua sendo a cópia fiel da tela antiga; o que muda de propósito fica AQUI, à
 * vista, e o teste de inventário lista cada exceção pelo nome.
 *
 * `frame_interval`: a tela recomendava 2000 ms. Desde 05/09/2026 as câmeras operam a
 * 1000 ms com captura persistente, e cada câmera tem intervalo próprio (tela Câmeras)
 * que prevalece sobre este — o que a descrição não dizia.
 */
export const AJUSTES_DE_TEXTO = {
  frame_interval: {
    label: 'Intervalo de Captura — valor geral (reserva)',
    description: 'Vale só para a câmera que NÃO tem intervalo próprio. O intervalo de cada câmera é definido no módulo Câmeras e prevalece sobre este. Valores menores aumentam a precisão, mas consomem mais recursos do servidor; em produção as câmeras operam a 1000 ms (um quadro por segundo) com captura persistente. Alterar este valor reinicia as câmeras, não o motor.',
    recommended: 1000,
  },
  // Onda 102: a descrição dizia "pixels do frame de 1280 px" e recomendava o piso como
  // filtro de carros ao fundo. Medido em 20/09/2026: a câmera de entrada entrega 1920 px e
  // a de saída 1280 — o mesmo número corta tamanhos reais diferentes —, e descartar placa
  // pequena perde mais veículos do que evita erros. O piso é rede de segurança.
  min_bbox_width: {
    description: 'Leituras com a placa mais estreita que este valor, em pixels da imagem, são descartadas mesmo com confiança alta. É uma rede de segurança contra o trânsito da rua caso a zona de leitura de uma câmera falhe — não um filtro de qualidade: a leitura descartada NÃO vira ticket. Medido em 20/09/2026 (30 dias): nenhuma leitura registrada tinha menos de 59 px, então 50 não descarta nada; na entrada, 7 de cada 12 placas abaixo de 75 px estavam certas, e na saída placas pequenas leem certo. As câmeras têm resoluções diferentes (entrada 1920 px, saída 1280 px): o mesmo número significa tamanhos diferentes em cada uma. Não suba este valor para "melhorar o OCR" — placa pequena que lê mal é tratada pela consolidação por passagem, que espera um quadro melhor. Use 0 para desativar.',
  },
};

/**
 * Onda 101 — os limites que o SERVIDOR impõe às chaves do motor, trazidos para o campo.
 *
 * Na primeira versão da fase 2 a recusa de um valor inválido só vinha do servidor, no
 * fim do caminho: o Iatagã pôs 99 no "Tempo limite interno do motor" (aceita 1 a 60) e
 * a tela ficou com 99, sem dizer nada. Quem sabe a faixa tem de dizer NA HORA, no campo.
 * O servidor continua conferindo (é ele quem manda); `onda101.test.js` prova que os dois
 * lados concordam: o servidor aceita o mínimo e o máximo daqui, e recusa o que passa.
 */
export const LIMITES_DO_MOTOR = {
  inference_timeout: { min: 1, max: 60 },
  snapshot_quality: { min: 30, max: 100 },
  luminance_night_max: { min: 0, max: 255 },
  luminance_dusk_max: { min: 0, max: 255 },
  luminance_ema_alpha: { min: 0.01, max: 1 },
  luminance_hysteresis: { min: 0, max: 30 },
  min_char_confidence: { min: 0, max: 1 },
  plate_crop_margin: { min: 0, max: 0.5 },
  layout_min_conf: { min: 0.5, max: 1 },
  layout_ocr_max_conf: { min: 0, max: 1 },
  discarded_min_conf: { min: 0, max: 1 },
  discarded_near_zone: { min: 0, max: 1 },
  discarded_throttle_s: { min: 0 },
  night_start_hour: { min: 0, max: 23 },
  night_end_hour: { min: 0, max: 23 },
  dusk_morning_start_hour: { min: 0, max: 23 },
  dusk_morning_end_hour: { min: 0, max: 23 },
  dusk_evening_start_hour: { min: 0, max: 23 },
  dusk_evening_end_hour: { min: 0, max: 23 },
};

const comAjustes = (base) => Object.fromEntries(Object.entries(base).map(
  ([k, m]) => [k, { ...m, ...(LIMITES_DO_MOTOR[k] || {}), ...(AJUSTES_DE_TEXTO[k] || {}) }]
));

export const SETTINGS_META = { ...comAjustes(META_BASE), ...META_PASSAGEM, ...META_FASE3, ...META_ONDA103, ...META_ONDA117, ...META_ONDA119, ...META_ONDA135, ...META_ONDA137, ...META_ONDA139, ...META_ONDA140, ...META_ONDA141, ...META_ONDA163, ...META_ONDA166, ...META_ONDA178, ...META_ONDA181 };

/**
 * As chaves que o servidor propaga ao ambiente do motor (`FASTALPR_ENV_MAP` em
 * `backend/src/routes/settings.js`): gravar qualquer uma REINICIA o motor — cerca
 * de um minuto sem leituras. A tela marca esses campos com um selo. O teste
 * `onda100.test.js` confere que esta lista e a do servidor são a mesma.
 */
export const CHAVES_DO_MOTOR = [
  'overexp_enabled', 'overexp_threshold', 'overexp_gamma',
  'dynamic_threshold_enabled',
  'confidence_threshold_day', 'confidence_threshold_dusk', 'confidence_threshold_night',
  'threshold_mode', 'luminance_night_max', 'luminance_dusk_max',
  'night_start_hour', 'night_end_hour',
  'dusk_morning_start_hour', 'dusk_morning_end_hour',
  'dusk_evening_start_hour', 'dusk_evening_end_hour',
  'min_char_confidence', 'plate_crop_margin', 'ocr_source',
  'denoise_mode', 'denoise_search_window',
  'layout_classifier_enabled', 'layout_min_conf', 'layout_ocr_max_conf',
  'save_plate_crops', 'save_discarded_evidence',
  'discarded_min_conf', 'discarded_throttle_s',
  'snapshot_quality', 'inference_timeout',
  'luminance_ema_alpha', 'luminance_overrides', 'luminance_hysteresis',
  'discarded_near_zone',
];

const _motor = new Set(CHAVES_DO_MOTOR);
export const reiniciaOMotor = (chave) => _motor.has(chave);

/**
 * A primeira frase da descrição, para a linha compacta; o resto fica atrás de
 * "saiba mais". Só corta em ponto final seguido de maiúscula — "0,03 a 0,08 s" e
 * "hybrid: ... (recomendado). luminance: ..." ficam inteiros.
 */
export function resumoDa(descricao) {
  const texto = String(descricao || '').trim();
  const m = texto.match(/^([\s\S]+?[.!?])\s+(?=[A-ZÀ-Ú"(⚠])/);
  if (!m || m[1].length === texto.length) return { resumo: texto, resto: '' };
  return { resumo: m[1], resto: texto.slice(m[0].length).trim() };
}
